import { CustomDrawer } from 'components/CustomDrawer/CustomDrawer';
import { IAiSummaryDrawerProps, HOME_STORIES, SCHOOL_KEYS, keyDisplay, AMENITY_MUTEX, SchoolKey } from './AiSummaryDrawer.def';
import React, { useCallback, useEffect, useRef } from 'react';
import { IFilters } from 'components/Filters/Filters.def';
import { splitIntoArray } from 'utils/helpers';
import { trackEvent } from 'services/api-client/matomoTracker';
import { ILabeledField } from 'operations/search-filter-operation';
import { getNeighborhoodDetailsById } from 'services';

type IsSelectedFunc = (key: keyof IFilters | 'forcl', value: string | ILabeledField<string>) => boolean;

const EMPTY_OBJ = {};

const toggleCategory = <T extends string,>(categories: T[], category: T) => {
  return categories.includes(category)
    ? categories.filter(c => c !== category)  // Remove if exists
    : [...categories, category];  // Add if not exists
};

// Single-select filter keys whose value is stored as an ILabeledField
// ({ value, label }) rather than a plain string. Chips carry the raw id in
// `data-val` and the display name in `data-label`; onChipClick rebuilds the
// object here (an object can't survive a DOM `data-*` attribute — it would
// stringify to "[object Object]").
const LABELED_KEYS: string[] = [...SCHOOL_KEYS, 'neighborhood', 'nid'];

// IFilters keys whose value is an array, not a comma-separated string.
// `fromUIToFilters` maps/joins over them directly, so storing a joined string
// here throws (".map is not a function") once the filters are sent.
const ARRAY_VALUED_KEYS: string[] = ['amenities', 'Stories', 'City', 'ZipCode', 'PropertyType', 'Category'];

// Normalize a held filter value (array, comma string, or number) into trimmed parts.
const toValueArray = (value: unknown): string[] => {
  const parts = Array.isArray(value) ? value : String(value ?? '').split(',');
  return parts.map((item) => String(item).trim()).filter(Boolean);
};

export const AiSummaryDrawer = (props: IAiSummaryDrawerProps) => {
  const { closeAiSummaryDrawer, data, loading, error, filters, setFilters, onSendMessage: onOriginalSendMessage } = props;

  const onSendMessage = useCallback((nextFilters: IFilters) => {
    trackEvent("AI Mode / Listing Search Apply AI Summary Filter");
    onOriginalSendMessage(nextFilters);
  }, [onOriginalSendMessage]);

  const aggregates = data?.aggregates ?? EMPTY_OBJ;
  const topPriceTrends = aggregates?.neighborhoods?.toppricetrends?.values || EMPTY_OBJ;

  const pendingSendRef = useRef(false);

  useEffect(() => {
    if (pendingSendRef.current) {
      pendingSendRef.current = false;
      onSendMessage(filters);
    }
  }, [filters, onSendMessage]);

  const computeNextFilters = useCallback(
    (prev: IFilters, key: string, rawValue: string | ILabeledField<string>): IFilters => {
      const next = { ...prev };

      /* ---------- A. labeled single-select (schools / district / neighborhood) ---------- */
      if (LABELED_KEYS.includes(key)) {
        const field: ILabeledField<string> =
          typeof rawValue === 'object' ? rawValue : { value: rawValue, label: '' };
        const current = next[key as keyof IFilters] as ILabeledField<string> | undefined;
        // If the same id is already selected, deselect it; otherwise set it.
        // Compare via String() (matching isSelected): a seeded value may be a
        // number while data-val rebuilds it as a string, and `===` would miss it.
        const isSame = current != null && String(current.value) === String(field.value);
        next[key as keyof IFilters] = (isSame ? undefined : field) as any;
        return next;
      }

      // Remaining branches operate on plain string values.
      const value = typeof rawValue === 'string' ? rawValue : rawValue.value;

      /* ---------- B. amenities special‐case (pool ↔ no_pool) ---------- */
      if (key === 'amenities') {
        const nextRecord = next as Record<string, any>;
        const current = toValueArray(next.amenities);

        // remove the mutually-exclusive counterpart, if any
        const opposite = AMENITY_MUTEX[value];
        const filtered = current.filter((v: string) => v !== opposite);

        // toggle current value
        const hasValue = filtered.includes(value);
        const amenities = hasValue
          ? filtered.filter((v: string) => v !== value) // deselect
          : [...filtered, value]; // select

        // Keep the array shape declared by IFilters — see ARRAY_VALUED_KEYS.
        if (amenities.length) nextRecord.amenities = amenities;
        else delete nextRecord.amenities;
        return next;
      }

      /* ---------- C. newconstr / forcl: Category array toggle ---------- */
      if (['newconstr', 'forcl'].includes(key)) {
        let categories = next?.Category || [];

        if (key === 'newconstr') {
          categories = toggleCategory(categories, 'new_constr2');
        }

        if (key === 'forcl') {
          categories = toggleCategory(categories, 'Foreclosure');
        }

        next.Category = categories;
        return next;
      }

      /* ---------- D. everything else: existing multi-select ---------- */
      const nextRecord = next as Record<string, any>;
      const items = toValueArray(nextRecord[key]);
      const remaining = items.includes(value)
        ? items.filter((v: string) => v !== value)
        : [...items, value];
      if (!remaining.length) delete nextRecord[key];
      // Array-valued keys (e.g. `Stories`) must stay arrays; the rest are
      // still stored as comma-separated strings.
      else nextRecord[key] = ARRAY_VALUED_KEYS.includes(key) ? remaining : remaining.join(',');
      return next;
    },
    []
  );

  const toggleFilterValue = useCallback(
    (key: string, value: string | ILabeledField<string>) => {
      pendingSendRef.current = true;
      setFilters((prev) => computeNextFilters(prev, key, value));
    },
    [computeNextFilters, setFilters]
  );

  const onChipClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    const name = e.currentTarget.getAttribute('data-name') || '';
    const val = e.currentTarget.getAttribute('data-val') || '';
    const label = e.currentTarget.getAttribute('data-label') || '';
    if (!name || !val) return;
    if (name === 'ptid') {
      getNeighborhoodDetailsById(val).then((neighborhood) => {
        if (!neighborhood?.nid) return;
        toggleFilterValue('nid', {
          value: neighborhood.nid,
          label: neighborhood.name || label,
        });
      });
      return;
    }

    // Labeled keys (schools / district / neighborhood) store { value, label };
    // rebuild the object from data-val (id) + data-label (name).
    const value: string | ILabeledField<string> = LABELED_KEYS.includes(name)
      ? { value: val, label }
      : val;
    toggleFilterValue(name, value);
  };

  const isSelected: IsSelectedFunc = (key, value) => {
    // `newconstr`/`forcl` toggle the shared `Category` array (computeNextFilters
    // branch C), so their selection state lives there — not under filters[key].
    if (key === 'NewConstr') return (filters.Category ?? []).includes('new_constr2');
    if (key === 'forcl') return (filters.Category ?? []).includes('Foreclosure');
    const target = typeof value === 'object' && value ? String(value.value) : String(value);
    const current = filters[key];
    // Labeled single-select fields store an { value, label } object.
    if (current && typeof current === 'object' && !Array.isArray(current) && 'value' in current) {
      return String((current as ILabeledField<string>).value) === target;
    }
    const items = splitIntoArray(current as any || "")
      .map((item: any) => item.trim())
      .filter(Boolean);
    return items.includes(target);
  };

  return (
    <CustomDrawer
      title={
        <div className="d-flex align-items-center">
          <img
            className="mr-2"
            style={{ width: '24px' }}
            src="https://content.harstatic.com/media/temporary/har_ai_invert_logo.png"
            alt="HAR AI Invert Logo"
          />
          <span className="font_weight--bold font_size--large">Summary</span>
          <span className="ml-1">(key insights in a concise summary)</span>
        </div>
      }
      closePopup={closeAiSummaryDrawer}
    >
      {loading && (
        <div className="text-center border_radius--pudgy bg_color_cement_light mt-5 p-4 ml-auto mr-auto" style={{ maxWidth: '450px' }}>
          <div className="d-flex align-items-center">
            <div className="spinner-border mr-4 color_har_blue_dark" style={{ width: '2rem', height: '2rem' }} role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <div className="font_size--medium color_auxiliary">Loading...</div>
          </div>
        </div>
      )}

      {!loading && error && <div className="text-danger">{error}</div>}

      {!loading && !error && (
        <>
          {data?.total ? (
            <div
              className="cardv2 p-3 position-relative ai_contents mb-4"
              id="aisearch_insights"
              style={{ height: 'auto', paddingBottom: '60px' }}
            >

              <div className="pt-3 font_size--medium">
                {data?.summary?.intro || ""}

                {aggregates["ammenities"] && (
                  <>
                    <div className="font_weight--bold font_size--large pt-4 pb-2">Home Amenities Breakdown</div>
                    <ul className="list--black pt-2">
                      {Object.entries(aggregates["ammenities"]).map((ammenity: any) => {
                        const [amenityKey, amenityData] = ammenity;
                        if (amenityKey === "private_pool") {
                          return (
                            <React.Fragment key={amenityKey}>
                              {amenityData?.values[0] && (
                                <li style={{ padding: "0px 0px 8px 23px" }}>
                                  <span
                                    className="aiaggs aggamen"
                                    data-val="0"
                                    data-name={amenityKey}
                                  >
                                    No {amenityData?.title}{" "}
                                    <span
                                      title={`You can only select one pool type at a time`}
                                      className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                      style={{
                                        backgroundColor: isSelected('amenities', 'no_pool')
                                          ? '#cce0ff'
                                          : '#dfe3f1'
                                      }}
                                      onClick={onChipClick}
                                      data-val={'no_pool'}
                                      data-name={'amenities'}
                                    >
                                      {amenityData?.values[0]?.doc_count} Listings
                                    </span>
                                  </span>
                                </li>
                              )}
                              {amenityData?.values[1] && (
                                <li style={{ padding: "0px 0px 8px 23px" }}>
                                  <span
                                    className="aiaggs aggamen"
                                    data-val="1"
                                    data-name={amenityKey}
                                  >
                                    {amenityData?.title}{" "}
                                    <span
                                      title={`You can only select one pool type at a time`}
                                      className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                      style={{
                                        backgroundColor: isSelected('amenities', 'pool')
                                          ? '#cce0ff'
                                          : '#dfe3f1'
                                      }}
                                      onClick={onChipClick}
                                      data-val={'pool'}
                                      data-name={'amenities'} // here i am using separator $ for split above in onChipClick to know what will be the aggregate key and what will be amenity key
                                    >
                                      {amenityData?.values[1]?.doc_count} Listings
                                    </span>
                                  </span>
                                </li>
                              )}

                            </React.Fragment>
                          );
                        }

                        // 2) If amenityKey is one of these special cases
                        else if (
                          ["culdesac", "waterfront", "waterview"].includes(amenityKey)
                        ) {
                          const title = amenityData?.title || '';
                          return (
                            <li style={{ padding: "0px 0px 8px 23px" }} key={amenityKey}>
                              <span
                                className="aiaggs aggamen"
                                data-val="1"
                                data-name={amenityKey}
                              >
                                {title?.toLowerCase().includes('locations') ? title : `${title} locations`}{" "}
                                <span
                                  className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                  style={{
                                    backgroundColor: isSelected('amenities', amenityKey)
                                      ? '#cce0ff'
                                      : '#dfe3f1'
                                  }}
                                  onClick={onChipClick}
                                  data-val={amenityKey}
                                  data-name={'amenities'}
                                >
                                  {amenityData?.values[1]?.doc_count} Listings
                                </span>
                              </span>
                            </li>
                          );
                        }

                        // 3) If amenityKey is "ingolfcom"
                        else if (amenityKey === "ingolfcom") {
                          return (
                            <li style={{ padding: "0px 0px 8px 23px" }} key={amenityKey}>
                              <span
                                className="aiaggs aggamen"
                                data-val="1"
                                data-name={amenityKey}
                              >
                                {amenityData?.title?.replace("Community", "community homes")}{" "}
                                <span
                                  className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                  style={{
                                    backgroundColor: isSelected('amenities', amenityKey)
                                      ? '#cce0ff'
                                      : '#dfe3f1'
                                  }}
                                  onClick={onChipClick}
                                  data-val={amenityKey}
                                  data-name={'amenities'}
                                >
                                  {amenityData?.values[1]?.doc_count} Listings
                                </span>
                              </span>
                            </li>
                          );
                        }

                        // 4) Otherwise just show the amenityKey normally
                        else {
                          return (
                            <li style={{ padding: "0px 0px 8px 23px" }} key={amenityKey}>
                              <span
                                className="aiaggs aggamen"
                                data-val="1"
                                data-name={amenityKey}
                              >
                                {amenityData.title}{" "}
                                <span
                                  className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                  style={{
                                    backgroundColor: isSelected('amenities', amenityKey)
                                      ? '#cce0ff'
                                      : '#dfe3f1'
                                  }}
                                  onClick={onChipClick}
                                  data-val={amenityKey}
                                  data-name={'amenities'}
                                >
                                  {amenityData.values[1].doc_count} Listings
                                </span>
                              </span>
                            </li>
                          );
                        }
                      })}
                    </ul>
                  </>

                )}

                {aggregates["attrib"] && (
                  <>
                    <div className="font_weight--bold font_size--large pb-2">Special Home Features Analysis</div>
                    <ul className="list--black pt-2">
                      {Object.entries(aggregates["attrib"]).map((attrib: any) => {
                        const [attribKey, attribData] = attrib;
                        if (attribData?.valid === true && attribData?.title) {
                          // STORIES
                          if (attribKey === "stories") {
                            return Object.entries(attribData?.values).map((attribData: any) => {
                              const [storyKey, storyData] = attribData;
                              if (['1', '2', '3', '4', '1.5'].includes(storyKey)) {
                                return (
                                  <li
                                    style={{ padding: "0px 0px 8px 23px" }}
                                    key={`${attribKey}-${storyKey}`}
                                  >
                                    <span
                                      className="aiaggs aggattrib"
                                      data-val={storyData?.val}
                                      data-label={`${storyData?.val} ${attribData?.title}`}
                                      data-name={attribKey}
                                    >
                                      {HOME_STORIES[storyKey as keyof typeof HOME_STORIES]}{" "}
                                      <span
                                        className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                        style={{
                                          backgroundColor: isSelected('Stories', storyData?.val)
                                            ? '#cce0ff'
                                            : '#dfe3f1'
                                        }}
                                        onClick={onChipClick}
                                        data-val={storyData?.val}
                                        data-label={`${storyData?.val} ${attribData?.title}`}
                                        data-name={`Stories`}
                                      >
                                        {storyData?.doc_count} Listings
                                      </span>
                                    </span>
                                  </li>
                                )
                              }
                              return null;
                            });
                          }
                          // FORECLOSURE
                          else if (attribKey === "forcl" && attribData?.simple === true) {
                            return (
                              <li
                                style={{ padding: "0px 0px 8px 23px" }}
                                key={attribKey}
                              >
                                <span
                                  className="aiaggs aggattrib"
                                  data-val={attribData?.values[1]?.val}
                                  data-label={attribData?.title}
                                  data-name={attribKey}
                                >
                                  Foreclosure Homes{" "}
                                  <span
                                    className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                    style={{
                                      backgroundColor: isSelected('forcl', attribData?.values[1]?.val)
                                        ? '#cce0ff'
                                        : '#dfe3f1'
                                    }}
                                    onClick={onChipClick}
                                    data-val={attribData?.values[1]?.val}
                                    data-label={attribData?.title}
                                    data-name={`forcl`}
                                  >
                                    {attribData?.values[1]?.doc_count} Listings
                                  </span>
                                </span>
                              </li>
                            );
                          }
                          // for new construction the key will be newconstr or new_constr2 i blade code is newconstr and in API response i am getting new_constr2
                          // NEW CONSTRUCTION
                          else if (attribKey === "newconstr" && attribData?.simple === true) {
                            const newConstrVal = attribData?.values[1];
                            return (
                              <li
                                style={{ padding: "0px 0px 8px 23px" }}
                                key={attribKey}
                              >
                                <span
                                  className="aiaggs aggattrib"
                                  data-val={"new_constr2"}
                                  data-label={attribData?.title}
                                  data-name={attribKey}
                                >
                                  New Construction Homes{" "}
                                  <span
                                    className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                    style={{
                                      backgroundColor: isSelected('NewConstr', newConstrVal)
                                        ? '#cce0ff'
                                        : '#dfe3f1'
                                    }}
                                    onClick={onChipClick}
                                    data-val={"new_constr2"}
                                    data-name={`newconstr`}
                                    data-label={attribData?.title}
                                  >
                                    {newConstrVal?.doc_count} Listings
                                  </span>
                                </span>
                              </li>
                            );
                          }
                        }
                        // If not matched by above conditions, return nothing (or null)
                        return null;
                      })}
                    </ul>
                  </>
                )}
                <EducationalEnvironmentInsights
                  aggregates={aggregates}
                  onChipClick={onChipClick}
                  isSelected={isSelected}
                />
                {Object.keys(topPriceTrends).length > 0 && (
                  <>
                    <div className="font_weight--bold font_size--large pb-2">
                      Neighborhoods
                    </div>
                    <p>
                      Here are the top neighborhoods by number of Homes based on your search criteria.
                      {Object.keys(topPriceTrends).length > 9 && (
                        <> Zoom in or add location filters to see more neighborhoods.</>
                      )}
                    </p>
                    <ul className="list--black pt-2">
                      {Object.entries(topPriceTrends).map((agitem: any, index: number) => {
                        const [key, value] = agitem;
                        const isApplied = !!value?.val && filters.nid?.label === value.val;
                        return (
                          <li key={index} style={{ padding: "0px 0px 8px 23px" }}>
                            <span
                              className="aiaggs aggpt"
                              data-val={value?.term}
                              data-label={value?.val}
                              data-name="ptid"
                            >
                              {value?.val}{" "}
                              <span
                                className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                                style={{
                                  backgroundColor: isApplied
                                    ? '#cce0ff'
                                    : '#dfe3f1'
                                }}
                                onClick={onChipClick}
                                data-val={value?.term}
                                data-label={value?.val}
                                // `ptid`, not `nid`: these buckets are keyed by pt_id — HAR
                                // returns `param: "ptids"` with the aggregation. onChipClick
                                // resolves it to the `nid` the search payload carries.
                                data-name="ptid"
                              >
                                {value?.doc_count} Listings
                              </span>
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  </>
                )}

              </div>

            </div>
          ) : (
            <div className="mt-4 mb-1 text-center align-self-center">
              <div id="noresults_image" className="text-center pb-3">
                <img
                  className="img-fluid mb-3 mt-4"
                  src="https://content.harstatic.com/media/artwork/illustration-404-error.svg"
                  alt="404 page"
                  style={{ width: 300, opacity: 0.28 }}
                />
              </div>

              <div className="font_size--large_extra_extra font_weight--semi_bold color_black pb-4 text-center">
                No homes found
              </div>

              <div
                className="font_size--medium color_black text-center ml-auto mr-auto mb-4"
                style={{ maxWidth: 320 }}
              >
                Try adjusting your search by changing / removing filters.
              </div>

            </div>
          )}
        </>
      )}
    </CustomDrawer>
  );
};



const EducationalEnvironmentInsights = ({ aggregates, isSelected, onChipClick }: { aggregates: any, isSelected: IsSelectedFunc, onChipClick: (event: React.MouseEvent<HTMLSpanElement>) => void }) => {

  const schools = aggregates?.schools;
  const schoolDistricts = aggregates?.schooldist;

  const hasSchools = schools && Object.keys(schools).length > 0;
  const hasSchoolDist = schoolDistricts && Object.keys(schoolDistricts).length > 0;

  if (!hasSchools && !hasSchoolDist) {
    return null;
  }
  return (
    <>
      {hasSchools && (
        <>
          <div className="font_weight--bold font_size--large pb-2">
            Educational Environment Insights
          </div>
          <p>
            Here are the top schools by number of Homes and their ratings.
            {Object.keys(schools).length > 10 && (
              <>Zoom in or add location filters to see more schools.</>
            )}
          </p>

          <ul className="list--black pt-2">
            {Object.entries(schools).map((school: any) => {
              const [schoolKey, schoolData] = school;
              if (schoolData?.meta?.Name && schoolData?.doc_count) {
                const name = schoolData?.meta?.Name.trim();

                let schoolType: SchoolKey | '' = '';
                if (schoolData?.schooltype === 'M') {
                  schoolType = `middle_school`;
                } else if (schoolData?.schooltype === 'H') {
                  schoolType = `high_school`;
                } else if (schoolData?.schooltype === 'E') {
                  schoolType = `elemantary_school`;
                }
                const val = schoolData?.val
                const nameWithValue = { value: val, label: name };
                const rating = schoolData?.meta?.ACCT_Rating;
                return (
                  <li key={schoolKey} style={{ padding: "0px 0px 8px 23px" }}>
                    <span
                      className="aiaggs aggschool"
                      data-title={name}
                      data-schtype={schoolData?.schooltype}
                      data-linktype="school"
                      data-term={val}
                    >
                      {name}{" "}
                      <span
                        title={`You can only select one ${keyDisplay[schoolType]} at a time`}
                        className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                        style={{
                          backgroundColor: schoolType && isSelected(schoolType, nameWithValue) ? '#cce0ff' : '#dfe3f1',
                          cursor: 'pointer',
                        }}
                        onClick={onChipClick}
                        data-schtype={schoolData?.schooltype}
                        data-linktype="school"
                        data-val={val}
                        data-label={name}
                        data-term={val}
                        data-name={schoolType}
                      >
                        {schoolData?.doc_count || 0} Homes {rating ? `(Rating: ${rating})` : '(Not Rated: SB 1365)'}
                      </span>
                    </span>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </>
      )}

      {/* If no schools but there are school districts (count>1), show heading/intro for districts */}
      {!hasSchools && hasSchoolDist && (
        <>
          <div className="font_weight--bold font_size--large pb-2">
            Educational Environment Insights
          </div>
          <p>
            Here are the top school districts by number of Homes and their ratings.
          </p>
        </>
      )}

      {/* If school districts exist, we show them below.
          Note the Blade snippet also re-introduces text if *both* exist. */}
      {hasSchoolDist && hasSchools && (
        <p>
          Here are the top school districts by number of Homes and their ratings in your search area.
        </p>
      )}

      {hasSchoolDist && (
        <ul className="list--black pt-2">
          {Object.entries(schoolDistricts).map((schoolDistrict: any) => {
            const [distKey, distData] = schoolDistrict;
            if (distData?.meta?.Name && distData?.doc_count) {
              const name = distData?.meta?.Name.trim();
              const val = distData?.val
              const rating = distData?.meta?.ACCT_Rating;
              const nameWithValue = { value: val, label: name };
              return (
                <li key={distKey} style={{ padding: "0px 0px 8px 23px" }}>
                  <span
                    className="aiaggs aggschool_district"
                    data-title={name}
                    data-linktype="school_district"
                    data-term={val}
                  >
                    {name}{" "}
                    <span
                      title={`You can only select one ${keyDisplay['school_district']} at a time`}

                      className="aiaggs border_radius--round color_har_blue_dark px-2 py-1 font_size--small_extra cursor--pointer"
                      style={{
                        backgroundColor: isSelected('school_district', nameWithValue) ? '#cce0ff' : '#dfe3f1',
                        cursor: 'pointer',
                      }}
                      onClick={onChipClick}
                      data-linktype="school_district"
                      data-val={val}
                      data-label={name}
                      data-term={val}
                      data-name={'school_district'}
                    >
                      {distData?.doc_count || 0} Homes {!rating || distData?.meta?.stars_text === "No Rating" ? "(Not Rated: SB 1365)" : `(Rating: ${rating})`}
                    </span>
                  </span>
                </li>
              );
            }
            return null;
          })}
        </ul>
      )}
    </>
  );
}